import * as Sentry from "@sentry/react";
import { getCLS, getFCP, getFID, getLCP, getTTFB } from "web-vitals";
import type { Metric } from "web-vitals";

type MonitoringUser = {
  uid: string;
  email?: string | null;
} | null;

type EventPayload = Record<string, string | number | boolean>;

const getSentryDsn = () => process.env.REACT_APP_SENTRY_DSN || "";

let hasInitialized = false;

function shouldCaptureWebVital(metric: Metric): boolean {
  switch (metric.name) {
    case "CLS":
      return metric.value > 0.1;
    case "FID":
      return metric.value > 100;
    case "FCP":
      return metric.value > 1800;
    case "LCP":
      return metric.value > 2500;
    case "TTFB":
      return metric.value > 800;
    default:
      return false;
  }
}

function captureWebVital(metric: Metric): void {
  if (!shouldCaptureWebVital(metric)) {
    return;
  }

  try {
    Sentry.captureMessage(`admin_web_vital_${metric.name.toLowerCase()}`, "warning");
    Sentry.addBreadcrumb({
      category: "performance",
      message: metric.name,
      level: "warning",
      data: {
        id: metric.id,
        value: metric.value,
        delta: metric.delta,
      },
    });
  } catch (error) {
    if (process.env.NODE_ENV !== "test") {
      console.warn("[monitoring] Failed to capture web vital", error);
    }
  }
}

function initWebVitals(): void {
  getCLS(captureWebVital);
  getFID(captureWebVital);
  getFCP(captureWebVital);
  getLCP(captureWebVital);
  getTTFB(captureWebVital);
}

export function initMonitoring(): void {
  if (hasInitialized) {
    return;
  }

  const dsn = getSentryDsn();
  if (!dsn) {
    if (process.env.NODE_ENV !== "test") {
      console.warn("[monitoring] REACT_APP_SENTRY_DSN not configured");
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV,
  });

  initWebVitals();
  hasInitialized = true;
}

export function setMonitoringUser(user: MonitoringUser): void {
  Sentry.setUser(
    user
      ? {
          id: user.uid,
          email: user.email || undefined,
        }
      : null,
  );
}

export function trackEvent(name: string, data?: EventPayload): void {
  try {
    Sentry.addBreadcrumb({
      category: "event",
      message: name,
      level: "info",
      data,
    });
  } catch (error) {
    if (process.env.NODE_ENV !== "test") {
      console.warn("[monitoring] Failed to track event", error);
    }
  }
}

export function captureError(
  error: unknown,
  context?: Record<string, string | number | boolean | null>,
): void {
  try {
    Sentry.captureException(error, {
      extra: context,
    });
  } catch (captureFailure) {
    if (process.env.NODE_ENV !== "test") {
      console.warn("[monitoring] Failed to capture error", captureFailure);
    }
  }
}
